/* ── User & auth types ── */

export type UserRole = "admin" | "manager" | "operator";

export type User = {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  active: boolean;
  tenant?: string;
  /**
   * Platform super-admin flag. Separate from `role` on purpose: `role` is
   * tenant-scoped (what you can do inside your own tenant) while
   * `platformAdmin` grants cross-tenant read/write — e.g. listing every
   * company, reading another tenant's memory/audit. Self-serve signups must
   * NEVER get this flag; only the bootstrap admin (ACC_INITIAL_ADMIN_*) and
   * a small hand-curated set of operators should.
   */
  platformAdmin?: boolean;
  lastLoginAt?: string;
  /** When the first-run wizard was completed (ISO). Absent = never. Kept for
   *  pre-207 compatibility; `onboardingStatus` is the richer state. */
  firstRunDoneAt?: string;
  /** Onboarding state machine (mig 207). Absent = "started" — the dashboard
   *  shows the wizard to tenant admins. "postponed" hides the auto-shown
   *  wizard but keeps the resume action; only "completed" is final. */
  onboardingStatus?: "started" | "postponed" | "completed";
  /** The goal chosen in the first-run wizard; drives the setup checklist. */
  onboardingGoal?: string;
  /** Server-side navigation work profile so it follows the user across
   *  devices (localStorage remains the same-browser fast path). */
  workProfile?: string;
  /** When the dashboard setup card was dismissed (mig 208). Absent = shown.
   *  Server-side so a dismissal follows the user to a new device. */
  onboardingCardDismissedAt?: string;
  createdAt: string;
  updatedAt: string;
};

/** Safe user object (no password hash) sent to the client */
export type SafeUser = Omit<User, "passwordHash">;

export type SessionPayload = {
  userId: string;
  email: string;
  name: string;
  role: UserRole;
  tenant?: string;
  platformAdmin?: boolean;
  /** This session passed a TOTP/recovery-code challenge (or was upgraded
   *  immediately after a confirmed 2FA enrollment). */
  mfa?: boolean;
  /** Limited setup-only session: the user is REQUIRED to enroll in 2FA but
   *  hasn't yet. The edge proxy and route guards wall this off from
   *  everything except the 2FA setup surface. */
  mfaSetup?: boolean;
  /** Session minted by the OIDC callback — MFA is the identity provider's
   *  responsibility there, so native-2FA enforcement exempts it. */
  sso?: boolean;
  /**
   * The tenant whose identity provider minted `sso` — i.e. the ONE workspace
   * that assurance speaks for.
   *
   * An OIDC provider belongs to a tenant, so "the IdP vouched for MFA" is a
   * statement about that workspace, not about every workspace the person can
   * enter. Since the tenant switch (docs/PLAN-tenant-memberships.md §11) an
   * `sso` session can be re-scoped to another workspace; without this claim
   * the per-request 2FA gate read `sso: true` and waved the session through
   * workspace B's `require_two_factor` on the word of workspace A's IdP —
   * exactly what security review F5 refused at switch time.
   *
   * Absent means "covers this session's own tenant" (a session minted by a
   * login-type path is always for the tenant it authenticated against), which
   * is also what a pre-upgrade token means. The switch pins it explicitly.
   */
  ssoTenant?: string;
  /**
   * Session lifetime claims — READ-ONLY. `verifyToken` hands back the raw JWT
   * payload, so a session resolved from a cookie carries the `iat`/`exp` the
   * token was signed with (seconds since epoch). `createToken` IGNORES both
   * and always sets fresh values, so passing them in a mint is a no-op; the
   * one path that must keep a lifetime intact — the tenant switch — goes
   * through `createTokenPreservingLifetime` (src/server/auth.ts), which
   * re-signs the same window so a re-scope can never extend a session or
   * outrun a `session_cutoffs` revocation.
   */
  iat?: number;
  exp?: number;
};

export const roleMeta: Record<UserRole, { label: string; cls: string; description: string }> = {
  admin:    { label: "Admin",    cls: "text-status-critical bg-status-critical/10 border-status-critical/20", description: "Full access" },
  manager:  { label: "Manager",  cls: "text-status-medium bg-status-medium/10 border-status-medium/20",       description: "View, assign, approve" },
  operator: { label: "Operator", cls: "text-status-info bg-status-info/10 border-status-info/20",             description: "View and work on assigned" },
};

/**
 * Permissions matrix.
 *
 * Scopes are deliberately narrow so grants can match the blast radius of the
 * action. For example, `codebases.manage` is separate from `agents.manage`
 * because a trusted CTO should be able to register a repo without also being
 * able to create/remove agents. Broader legacy scopes (`agents.manage`,
 * `users.edit`) remain so existing callers don't regress.
 */
export function can(role: UserRole, action: string): boolean {
  const permissions: Record<string, UserRole[]> = {
    // Users
    "users.list":   ["admin"],
    "users.create": ["admin"],
    "users.edit":   ["admin"],
    "users.delete": ["admin"],

    // Workspace flow
    "workspace.approve":  ["admin", "manager"],
    "workspace.reject":   ["admin", "manager"],
    "workspace.apply":    ["admin", "manager"],
    "workspace.create":   ["admin", "manager", "operator"],
    // Destroying somebody else's workspace is not a one-click operator
    // action — the run history, chat transcripts and patches die with it.
    // Restricted to admin/manager so a workspace started by one operator
    // can't be wiped by another from the same tenant.
    "workspace.delete":   ["admin", "manager"],
    // Rollback is admin-only — undoing an applied patch is a destructive
    // operation and should not be a one-click manager action.
    "workspace.rollback": ["admin"],

    // Issues
    "issues.assign": ["admin", "manager"],
    // Same logic as workspace.delete — destroying an issue (with its
    // attached email thread, patches, follow-ups) is admin/manager only.
    // An operator can still close an issue via PUT status=done.
    "issues.delete": ["admin", "manager"],

    // Verification / QA — who can flip checklist items and therefore clear
    // the `in_progress → done` gate. Restricted to admin/manager on purpose:
    // if every operator can self-mark blockers `passed`, the gate is theatre.
    "verification.mutate": ["admin", "manager"],
    // Starting a sandbox verification run. Mirrors @/server/policy — it was
    // missing here, so `can()` answered false for everyone and every "verify
    // in the sandbox" button (patch page, work session) stayed hidden while
    // the route itself would have accepted the click.
    "verification.run": ["admin", "manager", "operator"],

    // Agents
    "agents.manage": ["admin"],

    // Skills — how an agent does a task. Mirrors the server-side matrix in
    // @/server/policy so a hidden control matches what the route refuses.
    // Hiding a control is never the boundary; offering one that cannot work
    // is its own kind of lie. `skills.read` is deliberately absent here
    // because every authenticated role has it — `can()` returning false for
    // an unlisted action would gate a read that the route allows.
    "skills.edit":     ["admin", "manager"],
    "skills.create":   ["admin"],
    "skills.assign":   ["admin", "manager"],
    "skills.activate": ["admin"],
    "skills.install":  ["admin"],
    "skills.review":   ["admin"],

    // Codebases — filesystem/SSH roots agents can read. Admin-only for now,
    // but a dedicated scope means we can open this up later without
    // also granting agent management.
    "codebases.manage": ["admin"],

    // Project integration bindings — "this Slack channel / K8s namespace /
    // Freshdesk group belongs to project X". Changes routing for the whole
    // tenant, so it is NOT an operator-level triage action.
    "project.integrations.manage": ["admin", "manager"],

    // Project configuration — name, description, mode, guardrails, tags,
    // codebase linkage. Shapes routing/governance for the whole tenant, so
    // it is a manager-and-up concern. Mirrors the server-side `projects.manage`
    // capability in @/server/policy so the client gate matches what the
    // /api/projects route enforces.
    "projects.manage": ["admin", "manager"],

    // Audit trail — reading the tenant's audit log. Excludes operators (the
    // log carries role changes, logins and other sensitive actions); mirrors
    // the server-side `audit.read` capability in @/server/policy so a
    // client-side gate (e.g. an "Audit event" deep link) matches what the
    // /api/audit route will actually allow.
    "audit.read":       ["admin", "manager"],

    // Settings
    "settings.edit":    ["admin", "manager"],  // retention policy, tunables
    "settings.admin":   ["admin"],             // sensitive flags
    // Destructive + cross-tenant (platform-admin gated at the route): deletes
    // agent-run rows AND prunes audit history. Not run rows only.
    "settings.cleanup": ["admin"],
    "cli.login":        ["admin"],             // spawns server-side shells

    // Operational lifecycle (mig 271). Mirrors @/server/policy so a hidden
    // control matches what the route refuses. `resources.archive` is
    // visibility-only and reversible, hence operators; the other three are
    // governance acts. See the long note in @/server/policy CAP_ROLES.
    "resources.archive":        ["admin", "manager", "operator"],
    "governance.purge":         ["admin"],
    "retention.manage":         ["admin"],
    "evidence.read_historical": ["admin", "manager"],
  };
  return permissions[action]?.includes(role) ?? false;
}
